import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PermissionGroups, rng, UserImportPreviewRow } from '../../amorg-common';
import { importUsers, previewUserImport, uploadUserImportFile } from '../../api/adminClientApi';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { AmorgAdminPageProvider } from './AmorgAdminPageContext';
import { SingleColumnLayout } from '../../designSystem/layouts/SingleColumnLayout';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { AdminTab } from './_components/AdminTab';
import { H1, H2 } from '../../designSystem/typography/Heading';
import { SelectField } from '../../designSystem/formField/SelectField';
import { FormField } from '../../designSystem/formField/FormField';
import { TextInput } from '../../designSystem/inputs/TextInput';
import { FileUploadButton } from '../../designSystem/formField/FileUploadButton';
import { Button } from '../../designSystem/button/Button';
import { MessageCard } from '../../designSystem/Message';
import { Table } from '../../designSystem/Table';

export function AmorgAdminImportUsers (): JSX.Element
{
    const { siteId } = useParams<{ siteId: string }>();
    const navigate = useNavigate();

    const [loading, setLoading] = React.useState(false);
    const [permissionGroupId, setPermissionGroupId] = React.useState('user');
    const [password, setPassword] = React.useState('');
    const [slotId, setSlotId] = React.useState('');
    const [preview, setPreview] = React.useState<UserImportPreviewRow[] | null>(null);
    const [errorMessage, setErrorMessage] = React.useState('');
    const [successMessage, setSuccessMessage] = React.useState('');

    const onNoAccess = () => navigate('/amorgy/login');

    async function handleFileUpload (file: File)
    {
        setLoading(true);
        setErrorMessage('');
        setSuccessMessage('');
        setPreview(null);

        const newSlotId = rng.getRandomId();

        const uploadResult = await uploadUserImportFile(siteId || '', newSlotId, file, onNoAccess);
        if (uploadResult.status === 'failure')
        {
            setErrorMessage(uploadResult.clientMessage);
            setLoading(false);
            return;
        }

        const previewResult = await previewUserImport(siteId || '', newSlotId, onNoAccess);
        if (previewResult.status === 'error')
        {
            setErrorMessage(previewResult.clientMessage);
            setLoading(false);
            return;
        }

        setSlotId(newSlotId);
        setPreview(previewResult.users);
        setLoading(false);
    }

    async function handleImport ()
    {
        if (!slotId)
            return;

        setLoading(true);
        setErrorMessage('');
        setSuccessMessage('');

        const result = await importUsers(siteId || '', slotId, permissionGroupId, password, onNoAccess);
        if (result.status === 'failure')
        {
            setErrorMessage(result.clientMessage);
        }
        else
        {
            setSuccessMessage(`${preview?.length || 0} brugere er importeret`);
            setPreview(null);
            setSlotId('');
        }

        setLoading(false);
    }

    return (
        <AmorgAdminPageProvider>
            <LoadingPageOverlay loading={loading} />
            <SingleColumnLayout
                noDivider
                headerContainer={
                    <Flex flexDirection="column" gap={16}>
                        <AdminTab selectedTab='sites' />
                    </Flex>
                }

                contentContainer={
                    <Flex flexDirection="column" gap={16}>
                        <H1>Importér brugere</H1>

                        <SelectField
                            label="Rettighedsgruppe"
                            selectedValue={permissionGroupId}
                            options={[
                                { value: 'user', text: PermissionGroups.user.name },
                                { value: 'member', text: PermissionGroups.member.name }
                            ]}
                            onChange={(value) => setPermissionGroupId(value)}
                        />

                        <FormField
                            label="Adgangskode"
                            inputComponent={
                                <TextInput
                                    type="text"
                                    value={password}
                                    onChange={(value) => setPassword(value)}
                                />
                            }
                        />

                        <FormField
                            label="Excel fil (.xlsx)"
                            inputComponent={
                                <FileUploadButton
                                    inputName="uploaded_file"
                                    fileTypes={['xlsx']}
                                    text="Vælg fil"
                                    onClickUpload={handleFileUpload}
                                />
                            }
                        />

                        {errorMessage &&
                            <MessageCard variant="error" textAlign="left">
                                <span>{errorMessage}</span>
                            </MessageCard>
                        }

                        {successMessage &&
                            <MessageCard variant="success" textAlign="left">
                                <span>{successMessage}</span>
                            </MessageCard>
                        }

                        {preview &&
                            <Flex flexDirection="column" gap={16}>
                                <H2>{preview.length} brugere klar til import</H2>

                                <Table>
                                    <thead>
                                        <tr>
                                            <th>Fornavn</th>
                                            <th>Efternavn</th>
                                            <th>E-mail</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.map(user =>
                                            <tr key={user.email}>
                                                <td>{user.firstname}</td>
                                                <td>{user.lastname}</td>
                                                <td>{user.email}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>

                                <Button
                                    variant="filled"
                                    text="Godkend import"
                                    onClick={handleImport}
                                />
                            </Flex>
                        }
                    </Flex>
                }
            />
        </AmorgAdminPageProvider>
    );
}